import type React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Radio,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import type { Question, QuestionValidation } from "./QuizCreator";

interface QuestionFormProps {
  questionData: Question;
  validation?: QuestionValidation;
  onUpdate: (data: Question) => void;
}

const emptyValidation: QuestionValidation = {
  answer_option_errors: [],
};

export default function QuestionForm({
  questionData,
  validation = emptyValidation,
  onUpdate,
}: QuestionFormProps) {
  const handleFieldChange = (
    name: keyof Pick<Question, "question_text" | "points">,
    value: string,
  ) => {
    onUpdate({
      ...questionData,
      [name]:
        name === "points" ? Math.max(1, Number.parseInt(value || "1", 10)) : value,
    });
  };

  const handleTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    value: Question["question_type"] | null,
  ) => {
    if (!value) {
      return;
    }

    let answer_options = questionData.answer_options;

    if (value === "single_choice") {
      const firstCorrectIndex = answer_options.findIndex(
        (option) => option.is_correct,
      );

      answer_options = answer_options.map((option, index) => ({
        ...option,
        is_correct:
          firstCorrectIndex === -1 ? false : index === firstCorrectIndex,
      }));
    }

    onUpdate({
      ...questionData,
      question_type: value,
      answer_options,
    });
  };

  const handleAnswerOptionChange = (
    index: number,
    updates: Partial<Question["answer_options"][number]>,
  ) => {
    let answer_options = questionData.answer_options.map((option, optionIndex) =>
      optionIndex === index ? { ...option, ...updates } : option,
    );

    if (
      questionData.question_type === "single_choice" &&
      updates.is_correct === true
    ) {
      answer_options = answer_options.map((option, optionIndex) => ({
        ...option,
        is_correct: optionIndex === index,
      }));
    }

    onUpdate({
      ...questionData,
      answer_options,
    });
  };

  const handleCorrectSelection = (index: number, checked: boolean) => {
    if (questionData.question_type === "single_choice") {
      handleAnswerOptionChange(index, { is_correct: true });
      return;
    }

    handleAnswerOptionChange(index, { is_correct: checked });
  };

  const addAnswerOption = () => {
    onUpdate({
      ...questionData,
      answer_options: [
        ...questionData.answer_options,
        { option_text: "", is_correct: false },
      ],
    });
  };

  const removeAnswerOption = (index: number) => {
    onUpdate({
      ...questionData,
      answer_options: questionData.answer_options.filter(
        (_, optionIndex) => optionIndex !== index,
      ),
    });
  };

  return (
    <Stack spacing={3}>
      <TextField
        label="Question shown to learners"
        value={questionData.question_text}
        onChange={(event) =>
          handleFieldChange("question_text", event.target.value)
        }
        required
        fullWidth
        multiline
        minRows={2}
        error={Boolean(validation.question_text)}
        helperText={
          validation.question_text ||
          "Write the question exactly as learners should see it."
        }
      />

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel sx={{ mb: 1 }}>Answer type</FormLabel>
          <ToggleButtonGroup
            exclusive
            value={questionData.question_type}
            onChange={handleTypeChange}
            color="primary"
            size="small"
          >
            <ToggleButton value="single_choice">
              Single answer
            </ToggleButton>
            <ToggleButton value="multiple_choice">
              Multiple answers
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <TextField
          label="Points"
          type="number"
          value={questionData.points}
          onChange={(event) => handleFieldChange("points", event.target.value)}
          required
          error={Boolean(validation.points)}
          helperText={validation.points || "Points awarded for a correct answer."}
          inputProps={{ min: 1 }}
          sx={{ width: { xs: "100%", md: 160 } }}
        />
      </Stack>

      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Answer Choices
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {questionData.question_type === "single_choice"
                ? "Choose the one answer learners must select."
                : "Tick every answer learners must select."}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={addAnswerOption}
          >
            Add Choice
          </Button>
        </Box>

        <Stack spacing={1.5}>
          {questionData.answer_options.map((option, index) => {
            const optionValidation =
              validation.answer_option_errors?.[index]?.option_text;

            return (
              <Box
                key={`option-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  border: "1px solid",
                  borderColor: option.is_correct ? "success.main" : "divider",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  bgcolor: option.is_correct ? "success.light" : "transparent",
                  opacity:
                    questionData.answer_options.length <= 2 &&
                    !option.option_text &&
                    !option.is_correct
                      ? 0.95
                      : 1,
                }}
              >
                <Box sx={{ pt: 0.75 }}>
                  {questionData.question_type === "single_choice" ? (
                    <Radio
                      checked={option.is_correct}
                      onChange={() => handleCorrectSelection(index, true)}
                      inputProps={{
                        "aria-label": `Mark choice ${index + 1} as correct`,
                      }}
                    />
                  ) : (
                    <Checkbox
                      checked={option.is_correct}
                      onChange={(event) =>
                        handleCorrectSelection(index, event.target.checked)
                      }
                      inputProps={{
                        "aria-label": `Mark choice ${index + 1} as correct`,
                      }}
                    />
                  )}
                </Box>

                <TextField
                  value={option.option_text}
                  onChange={(event) =>
                    handleAnswerOptionChange(index, {
                      option_text: event.target.value,
                    })
                  }
                  placeholder={`Choice ${index + 1}`}
                  required
                  fullWidth
                  size="small"
                  error={Boolean(optionValidation)}
                  helperText={optionValidation}
                />

                <IconButton
                  onClick={() => removeAnswerOption(index)}
                  color="error"
                  size="small"
                  disabled={questionData.answer_options.length <= 2}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Stack>

        <FormHelperText error={Boolean(validation.answer_options)}>
          {validation.answer_options ||
            "Each question needs at least two answer choices."}
        </FormHelperText>
        <FormHelperText error={Boolean(validation.correct_answers)}>
          {validation.correct_answers}
        </FormHelperText>
      </Box>
    </Stack>
  );
}
